// utils/localStorage.ts
import { APP_STATE_STORAGE_KEY } from '../const/local-storage-keys.const';

/**
 * Type-safe localStorage utility with error handling
 */
export class LocalStorageUtil {
  /**
   * Check if localStorage is available
   */
  private static isAvailable(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get an item from localStorage with type safety
   */
  static getItem<T>(key: string): T | null {
    if (!this.isAvailable()) {
      console.warn('localStorage is not available');
      return null;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) return null;
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Error reading from localStorage key "${key}":`, error);
      return null;
    }
  }

  /**
   * Set an item in localStorage
   */
  static setItem<T>(key: string, value: T): boolean {
    if (!this.isAvailable()) {
      console.warn('localStorage is not available');
      return false;
    }

    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error(`Error writing to localStorage key "${key}":`, error);
      return false;
    }
  }

  /**
   * Remove an item from localStorage
   */
  static removeItem(key: string): boolean {
    if (!this.isAvailable()) {
      console.warn('localStorage is not available');
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
      return false;
    }
  }

  /**
   * Clear all localStorage items
   */
  static clear(): boolean {
    if (!this.isAvailable()) {
      console.warn('localStorage is not available');
      return false;
    }

    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  }

  /**
   * Get all keys from localStorage
   */
  static getAllKeys(): string[] {
    if (!this.isAvailable()) {
      console.warn('localStorage is not available');
      return [];
    }

    try {
      return Object.keys(localStorage);
    } catch (error) {
      console.error('Error getting localStorage keys:', error);
      return [];
    }
  }

  /**
   * Check if a key exists in localStorage
   */
  static hasItem(key: string): boolean {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error(`Error checking localStorage key "${key}":`, error);
      return false;
    }
  }

  /**
   * Get the size of localStorage in bytes (approximate)
   */
  static getSize(): number {
    if (!this.isAvailable()) {
      return 0;
    }

    try {
      let total = 0;
      for (const key in localStorage) {
        if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
          total += localStorage[key].length + key.length;
        }
      }
      return total;
    } catch (error) {
      console.error('Error calculating localStorage size:', error);
      return 0;
    }
  }

  /**
   * Get an item with a default value if not found
   */
  static getItemWithDefault<T>(key: string, defaultValue: T): T {
    const item = this.getItem<T>(key);
    return item !== null ? item : defaultValue;
  }

  /**
   * Update an existing item (only if it exists)
   */
  static updateItem<T>(key: string, value: T): boolean {
    if (!this.hasItem(key)) {
      console.warn(`Key "${key}" does not exist in localStorage`);
      return false;
    }
    return this.setItem(key, value);
  }
}

// Hook for using localStorage in React components
import { useState, useEffect } from 'react';

/**
 * React hook for localStorage with automatic state synchronization
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void, () => void] {
  // Get initial value from localStorage or use provided initial value
  const [storedValue, setStoredValue] = useState<T>(() => {
    const item = LocalStorageUtil.getItem<T>(key);
    return item !== null ? item : initialValue;
  });

  // Function to update both state and localStorage
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function for same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      LocalStorageUtil.setItem(key, valueToStore);
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  // Function to remove the item
  const removeValue = () => {
    try {
      LocalStorageUtil.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  };

  // Listen for changes in other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue));
        } catch (error) {
          console.error(`Error parsing localStorage change for key "${key}":`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [storedValue, setValue, removeValue];
}

// Type definitions for common use cases

export interface AppState {
  theme: 'light' | 'dark';
  language: string;
}

// Pre-configured functions for common patterns
export const useAppStateStorage = {
  get: () =>
    LocalStorageUtil.getItemWithDefault<AppState>(APP_STATE_STORAGE_KEY, {
      theme: 'light',
      language: 'en',
    }),
  set: (state: AppState) => LocalStorageUtil.setItem(APP_STATE_STORAGE_KEY, state),
  setTheme: (theme: 'light' | 'dark') => {
    const current = useAppStateStorage.get();
    return LocalStorageUtil.setItem(APP_STATE_STORAGE_KEY, { ...current, theme });
  },
  setLanguage: (language: string) => {
    const current = useAppStateStorage.get();
    return LocalStorageUtil.setItem(APP_STATE_STORAGE_KEY, { ...current, language });
  },
};
